import { z } from 'zod';
import { recapMetricSchema, recapOpenAskSchema } from './monday-recap.js';
// Brand memo — spec §0a.3. The canonical weekly artifact mag publishes
// per brand. Folds in friday_brief (honest performance + got_wrong) and
// monday_recap (this_week + texture) into a single section-structured
// memo, while adding sections those rituals don't have today (thesis,
// trust_check, hero ask).
//
// Frozen at publish: the prose doesn't change. Numbers are recomputed
// inline when the dashboard re-renders, but the agent's writing is
// what mag said at the time.
//
// Lifecycle:
//   1. runWeeklyMemoCycle (Sunday 11pm brand-tz) calls the memo agent
//      with the period's inputs.
//   2. Critic blocks publication on sycophancy / missing rationale /
//      claim safety (same critic pipeline §4.1 uses on every outbound).
//   3. The row writes, frozen_at = generated_at + ε (no post-publish
//      edit path yet).
//   4. Fan-out renderers ship to every member's primary channel.
export const brandMemoPeriodKindSchema = z.enum(['weekly', 'daily']);
/** One reading-section paragraph. The agent emits a small number
 *  of these so the prose stays scannable. Each block has a heading
 *  + body — the renderer keys on heading_kind for layout. */
export const brandMemoReadingBlockSchema = z.object({
    /** Stable kind so renderers + future migrations key on it instead
     *  of fuzzy-matching heading text. */
    kind: z.enum([
        'how_im_reading',
        'whats_working',
        'whats_not_working',
        'what_i_got_wrong',
        'whats_changing',
    ]),
    /** Section heading rendered above the body. Lowercase mag voice. */
    heading: z.string().min(1).max(80),
    /** 1-3 paragraphs of mag-voice prose. Markdown allowed but kept
     *  light (no headings inside; one level of bold/italic max). */
    body: z.string().min(1).max(2000),
});
/** One receipt — something mag shipped during the period. */
export const brandMemoReceiptSchema = z.object({
    /** Mirror of the underlying channel-event kind so the renderer
     *  can colour by channel. The LLM occasionally emits kinds outside
     *  this list (e.g. "manual_post_drafted", "site_re_extracted") —
     *  `.catch('other')` coerces those to the catch-all rather than
     *  failing the whole memo. The renderer treats 'other' as the
     *  generic mag-voice line; no information loss visible to the
     *  founder. */
    kind: z
        .enum([
        'meta_paid_ad_launched',
        'meta_paid_ad_paused',
        'google_search_ad_launched',
        'google_search_ad_paused',
        'instagram_post_published',
        'creative_drop_published',
        'brain_evolution_applied',
        'budget_reallocated',
        'other',
    ])
        .catch('other'),
    /** Mag-voice one-liner — what shipped + the read on it. Lowercase. */
    summary: z.string().min(1).max(280),
    /** Deep link into the detail view for this receipt (campaign,
     *  deliverable, brain version, etc.). Empty when there's no
     *  meaningful drill-down. */
    deepLink: z.string().max(512),
    /** Optional id linking back to the source row — useful for the
     *  renderer's "show me this" affordance. */
    sourceId: z.string().max(120).optional(),
});
/** Numbers block — same structure friday-brief + monday-recap use,
 *  plus mag's verdict line. */
export const brandMemoNumbersSchema = z.object({
    /** Up to 5 weekly metric tiles (spend, conversions, etc.) — same
     *  shape monday-recap uses. */
    metrics: z.array(recapMetricSchema).max(5),
    /** Mag's prose read on the numbers. One sentence ("pre-signal,
     *  holding until 1k imp"). Always present even on cold brands. */
    verdict: z.string().min(1).max(280),
});
/** Asks block — a hero ask + a batched group. Per spec, hard cap:
 *  1 hero + 4 batched. If mag wants more, she's over-asking. */
export const brandMemoAsksSchema = z.object({
    /** The one ask that owns the founder's attention this period.
     *  Null when there are zero open asks — that's the "you're clear"
     *  case the renderer celebrates. */
    hero: recapOpenAskSchema.nullable(),
    /** ≤4 small asks the founder can clear in a batch. Empty allowed. */
    batched: z.array(recapOpenAskSchema).max(4),
});
/** One campaign-level thesis — what mag is testing this period. */
export const brandMemoCampaignThesisSchema = z.object({
    campaignId: z.string().uuid(),
    campaignName: z.string().min(1).max(200),
    /** Mag's one-sentence hypothesis. Lowercase mag voice. */
    hypothesis: z.string().min(1).max(280),
    /** What signal would tell mag the hypothesis is right or wrong.
     *  Optional — early-cycle campaigns may not have a sharp test yet. */
    signalToWatch: z.string().min(1).max(280).optional(),
});
/** Trust check block — mag's current authority + any drops since
 *  the last memo + clean-streak progress on recovering drops. */
export const brandMemoTrustStateSchema = z.object({
    /** Mag-voice summary of the current authority state. ("autopause,
     *  hook rotation, ±15% within-campaign budget — all yours; new
     *  campaigns + budget raises still need your tap.") */
    summary: z.string().min(1).max(500),
    /** Authority drops that happened during this period. Empty on
     *  clean weeks. Each entry names what dropped + why. */
    drops: z
        .array(z.object({
        decisionType: z.string().min(1).max(80),
        from: z.enum(['A0', 'A1', 'A2', 'A3']),
        to: z.enum(['A0', 'A1', 'A2', 'A3']),
        reason: z.string().min(1).max(280),
    }))
        .max(10),
    /** This period's autonomous spend vs the brand's monthly budget.
     *  Surfaces the spec §0a.2 mistake-budget line. */
    autonomousSpendUsd: z.number().min(0),
    /** Monthly autonomous-spend cap for the brand. Optional because
     *  not every brand has set one — the agent re-emits whatever the
     *  context block declares, which can be `null` when no envelope
     *  exists. nullish() + transform folds null into undefined so the
     *  downstream type stays `number | undefined` while still
     *  accepting both wire shapes. */
    monthlyAutonomousBudgetUsd: z
        .number()
        .min(0)
        .nullish()
        .transform((v) => v ?? undefined),
    /** Per-lane spend breakdown — spec §0b.5. One entry for the
     *  always-on lane (when it has any spend or budget) plus one per
     *  campaign that actually committed autonomous spend this month.
     *  Empty array on cold brands. The agent re-emits this verbatim
     *  in its output. */
    spendByLane: z
        .array(z.object({
        lane: z.enum(['always_on', 'campaign']),
        /** Set when lane === 'campaign'. */
        campaignId: z.string().uuid().optional(),
        /** Set when lane === 'campaign'. */
        campaignName: z.string().min(1).max(200).optional(),
        spentUsd: z.number().min(0),
        /** Per-lane cap (always_on_paid_budget for the always-on
         *  lane, paid_budget for a campaign lane). Optional —
         *  not every lane has an envelope set. Same nullish-folding
         *  shape as `monthlyAutonomousBudgetUsd` above. */
        budgetUsd: z
            .number()
            .min(0)
            .nullish()
            .transform((v) => v ?? undefined),
    }))
        .max(20)
        .optional(),
});
/** What the agent returns. The scheduler fills in non-agent fields
 *  (period_start, brand_id, etc.) before persisting. */
export const brandMemoAgentOutputSchema = z.object({
    /** One sentence verdict on the period. Lowercase mag voice. The
     *  pinned card on /home shows this above the fold. */
    headline: z.string().min(1).max(280),
    /** 2-3 reading blocks. Order is meaningful — the renderer stacks
     *  them in array order. */
    reading: z.array(brandMemoReadingBlockSchema).min(1).max(4),
    /** Receipts mag wrote about the period. 0-12. Empty allowed (slow
     *  weeks happen) — renderer shows a "no big moves" line. */
    receipts: z.array(brandMemoReceiptSchema).max(12),
    /** Numbers block — agent authors the verdict line; metrics are
     *  pre-aggregated and passed in as context. The agent re-emits
     *  them in its output so the persisted memo carries a snapshot. */
    numbers: brandMemoNumbersSchema,
    /** Asks — hero + batched. The agent doesn't pick which open asks
     *  go hero vs batched arbitrarily; the prompt rules them based on
     *  card priority + recency. */
    asks: brandMemoAsksSchema,
    /** Per-campaign theses. One per active campaign. Capped at 8 to
     *  keep the section scannable. */
    thesisByCampaign: z.array(brandMemoCampaignThesisSchema).max(8),
    /** Trust check block. Drops + spend usage are agent-authored
     *  observations; the underlying authority data is supplied as
     *  context. */
    trustCheck: brandMemoTrustStateSchema,
});
/** The full persisted memo, as returned by the API. Wraps the agent
 *  output with scheduler-set fields + db row ids. */
export const brandMemoSchema = z.object({
    id: z.string().uuid(),
    brandId: z.string().uuid(),
    organizationId: z.string().uuid(),
    /** ISO date of the period's start (Monday brand-tz local for
     *  weekly memos). The dedup key — at most one memo per (brand,
     *  period_start, period_kind). */
    periodStart: z.string().date(),
    periodKind: brandMemoPeriodKindSchema,
    /** Brand's tz the scheduler fired in. Stored so the renderer
     *  can label "week of Mon dd in tz" without re-deriving. */
    tz: z.string().min(1).max(60),
    generatedAt: z.string().datetime(),
    /** Frozen at publish (= generated_at + critic-pass + persist).
     *  Renderers gate on this for the rare case where a memo is mid-
     *  generation (`generatedAt` set, `frozenAt` null). */
    frozenAt: z.string().datetime().nullable(),
    headline: z.string().min(1).max(280),
    reading: z.array(brandMemoReadingBlockSchema),
    receipts: z.array(brandMemoReceiptSchema),
    numbers: brandMemoNumbersSchema,
    asks: brandMemoAsksSchema,
    thesisByCampaign: z.array(brandMemoCampaignThesisSchema),
    trustCheck: brandMemoTrustStateSchema,
    /** LLM cost in cents (rounded). Useful for the cost surface +
     *  ops dashboards. */
    costCents: z.number().int().min(0),
    llmModelId: z.string().min(1).max(80),
});
export const brandMemoListResponseSchema = z.object({
    memos: z.array(brandMemoSchema),
});
//# sourceMappingURL=brand-memo.js.map